/**
 * Exercise registry — single source of truth for both built-in and user-defined
 * exercises. Pages query this instead of hardcoding metadata.
 *
 * - Built-ins: hardcoded BUILT_IN_EXERCISES, route to dedicated pages.
 * - Customs: stored in IndexedDB (`customExercises` table), route to a generic
 *   template page based on their `template` field.
 */
import { pageLinks } from '../pageLinks'
import { db, type LocalCustomExercise, type CustomExerciseTemplate } from './db'

export type ExerciseType = 'aerobic' | 'anaerobic'
export type ColorTheme = 'mint' | 'violet'

export interface ExerciseMeta {
  exerciseKey: string
  label: string
  icon: string
  color: ColorTheme
  exerciseType: ExerciseType
  path: string
  isCustom: boolean
  template?: CustomExerciseTemplate
  restDuration?: number | null
}

// ============================================================================
// Preset exercises — curated recommended exercises with fixed keys.
// Adding one creates a customExercises row with the corresponding preset_<slug>
// key, so it syncs as a regular custom exercise but stays identifiable across
// devices (same slug → same row).
// ============================================================================

export type PresetCategory = 'aerobic' | 'chest' | 'back' | 'shoulders' | 'arms' | 'legs' | 'core' | 'full_body'

export interface PresetExercise {
  exerciseKey: string                       // 'preset_running' or stable builtin key like 'plank'
  name: string
  icon: string
  template: CustomExerciseTemplate
  exerciseType: ExerciseType
  restDuration: number | null
  category: PresetCategory
  /** Per-set duration default — only meaningful for countdown template. */
  defaultDuration?: number | null
  /** Default kg — only meaningful for sets_with_rest template. */
  weight?: number | null
  /** Default quick-tap reps. Only meaningful for manual_reps template. */
  quickReps?: [number, number, number]
  /** Optional bullet-point form tips, shown via the 💡 button on the workout page. */
  tips?: string[]
}

/** Display order + Chinese labels for the picker UI. */
export const CATEGORY_DISPLAY: Array<{ key: PresetCategory; label: string }> = [
  { key: 'aerobic', label: '有氧' },
  { key: 'chest', label: '胸' },
  { key: 'back', label: '背' },
  { key: 'shoulders', label: '肩' },
  { key: 'arms', label: '手臂' },
  { key: 'legs', label: '腿' },
  { key: 'core', label: '核心' },
  { key: 'full_body', label: '全身' },
]

export const PRESET_EXERCISES: PresetExercise[] = [
  // ---- Aerobic ----
  { exerciseKey: 'stair_climbing', name: '爬楼梯', icon: 'lucide:footprints', template: 'laps_timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '全脚掌着地，避免踮脚踩楼梯边缘',
      '抬头挺胸，核心收紧，避免弯腰驼背',
      '速度保持均匀，呼吸跟上节奏',
      '下楼对膝盖压力大，建议只算上楼那段',
      '可手扶栏杆但不要过度借力',
      '心率过高时立刻减速；老旧楼梯注意防滑',
    ],
  },
  { exerciseKey: 'preset_running', name: '跑步', icon: 'lucide:rabbit', template: 'laps_timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '前脚掌或全脚掌先着地，避免后脚跟硬砸',
      '步频比步幅重要，目标 170-180 步/分钟',
      '上身放松，肩膀下沉，肘部 90° 自然前后摆动',
      '视线看前方 5-10 米，不要低头看脚',
      '呼吸节奏可尝试 2 步吸 2 步呼',
      '跑前热身 5 分钟，结束后慢走整理',
    ],
  },
  { exerciseKey: 'cycling', name: '骑车', icon: 'lucide:bike', template: 'timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '调整座椅高度：脚踏到最低点时膝盖微弯 (约 15°)',
      '上身略前倾，手肘微弯吸震',
      '蹬踏全程发力，不只是下踩，上拉同样输出',
      '踏频维持 70-90 转/分钟',
      '不要锁死膝盖',
      '长时间骑后下来活动一下髋部和腰',
    ],
  },
  { exerciseKey: 'preset_jump_rope', name: '跳绳', icon: 'lucide:rotate-cw', template: 'timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '前脚掌发力，跳起 2-3 cm 即可，不要过高',
      '手腕主导转动，肘部贴近身体不要大幅摆动',
      '落地轻盈，膝盖微弯吸震',
      '上身保持挺直，目视前方',
      '节奏从慢开始，逐步加快',
      '绳长合适：双脚踩中绳子时手柄到胸口',
    ],
  },
  { exerciseKey: 'preset_elliptical', name: '椭圆机', icon: 'lucide:infinity', template: 'timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '全脚掌踩在踏板上，避免踮脚',
      '上身挺直，核心收紧',
      '手脚配合发力，不要只用腿',
      '阻力适中：维持稳定节奏的同时略感费力',
      '可正向蹬也可反向蹬，锻炼不同肌群',
      '控制呼吸节奏，避免憋气',
    ],
  },
  { exerciseKey: 'preset_rowing', name: '划船机', icon: 'lucide:waves', template: 'timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '起始位置：膝盖弯曲、前倾、手臂伸直握把',
      '发力顺序：腿先发力 → 髋部打开 → 手臂拉至腹部',
      '回程顺序相反：手臂伸出 → 髋部前倾 → 膝盖弯曲',
      '全程保持背部挺直，不要弓背',
      '节奏：发力快、回程慢（约 2:1 比例）',
      '把柄拉到肋骨下方（胸腔下缘），不要拉到脖子',
    ],
  },
  { exerciseKey: 'preset_spinning', name: '动感单车', icon: 'lucide:gauge-circle', template: 'timer', exerciseType: 'aerobic', restDuration: null, category: 'aerobic',
    tips: [
      '调整座椅和把手高度，避免腰背紧张',
      '核心保持紧张，不要全靠把手支撑体重',
      '蹬踏全程平稳，不要"砸"踏板',
      '坐姿/站姿对应不同阻力',
      '大阻力站姿时膝盖不超过脚尖',
      '跟上节奏但不憋气',
    ],
  },

  // ---- Chest ----
  { exerciseKey: 'push_up', name: '俯卧撑', icon: 'lucide:arrow-down-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'chest', quickReps: [10, 15, 20],
    tips: [
      '手位略宽于肩，与胸齐平',
      '全程保持身体一条直线（不塌腰、不撅臀）',
      '核心收紧，肩胛骨主动下沉',
      '下降时肘部 45° 外展（不完全贴身、不外展 90°）',
      '胸部触地（或接近）后再撑起',
      '呼吸：下降吸气，上推呼气',
    ],
  },
  { exerciseKey: 'preset_dumbbell_bench_press', name: '哑铃卧推', icon: 'lucide:chevrons-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'chest', weight: 10, quickReps: [8, 10, 12],
    tips: [
      '仰卧平板凳，双脚踩实地面',
      '五点固定：头、上背、臀、双脚',
      '哑铃下放到胸侧，肘关节略低于肩',
      '推起时双手轨迹略向中线靠拢但不要让哑铃在顶部碰撞',
      '推起呼气、下放吸气',
      '肩胛骨后收下沉，保护肩关节',
    ],
  },
  { exerciseKey: 'preset_barbell_bench_press', name: '杠铃卧推', icon: 'lucide:chevrons-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 90, category: 'chest', weight: 40, quickReps: [6, 8, 10],
    tips: [
      '仰卧平板凳，眼睛位于杠铃正下方，双脚踩实地面',
      '握距略宽于肩，手腕保持中立，杠铃压在掌根',
      '肩胛骨后收下沉，上背微拱，臀部始终贴凳',
      '下放时控制速度，杠铃落在胸骨下缘附近，肘部约 45–75° 外展',
      '推起呼气，杠铃沿略向后上方的轨迹回到肩部正上方',
      '大重量务必有保护者或使用安全杠，不要独自做到力竭',
    ],
  },
  { exerciseKey: 'preset_dumbbell_fly', name: '哑铃飞鸟', icon: 'lucide:expand', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'chest', weight: 5, quickReps: [10, 12, 15],
    tips: [
      '手肘略弯 (10-20°)，全程保持这个角度',
      '下放时哑铃在胸侧画大弧线，到肩平面附近',
      '收起时想象"抱大树"，挤压胸肌中部',
      '不是"推上去"，是用胸肌收缩"拉过来"',
      '宁可用较轻的重量，重质量轻重量',
      '顶峰停顿 1 秒主动挤压',
    ],
  },

  // ---- Back ----
  { exerciseKey: 'preset_pull_up', name: '引体向上', icon: 'lucide:arrow-big-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 90, category: 'back', quickReps: [3, 5, 8],
    tips: [
      '正握，手位略宽于肩',
      '启动时主动下沉肩胛，再发力拉起',
      '用背阔肌发力，不要只靠手臂',
      '下巴超过单杠为完整一次',
      '下放时全程控制，不要靠惯性下坠',
      '保持核心收紧，避免身体大幅晃动',
    ],
  },
  { exerciseKey: 'preset_close_grip_pull_up', name: '引体向上（窄握）', icon: 'lucide:arrow-big-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 90, category: 'back', quickReps: [3, 5, 8],
    tips: [
      '手位与肩同宽或更窄，可反握或对握',
      '更偏重肱二头肌和下背阔肌发力',
      '启动同样要先下沉肩胛，再拉起',
      '下巴拉过杠为一个完整动作',
      '强度比正握大，可作为正握的进阶',
      '注意手腕负担，姿势要稳',
    ],
  },
  { exerciseKey: 'preset_lat_pulldown', name: '高位下拉', icon: 'lucide:arrow-big-down', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'back', weight: 30, quickReps: [8, 10, 12],
    tips: [
      '坐姿，大腿牢牢卡在护垫下，双脚踩实地面',
      '握距约为肩宽 1.5 倍，正握横杠',
      '先下沉、后收肩胛骨，再用背阔肌把横杠拉向锁骨/上胸',
      '上身略后仰（约 10–15°）即可，不要大幅后仰借力',
      '肘部向下、向身体两侧带，想象用手肘去拉，而不是手',
      '回放时慢且受控，手臂接近伸直、充分拉伸背部，但肩不要耸起',
    ],
  },
  { exerciseKey: 'preset_barbell_row', name: '杠铃划船', icon: 'lucide:rows-3', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 90, category: 'back', weight: 30, quickReps: [6, 8, 10],
    tips: [
      '屈髋俯身，背部挺直与地面约 45°',
      '双手与肩同宽握杠',
      '杠铃拉至下腹（肚脐附近），肘部贴近身体',
      '顶峰挤压背阔肌，停顿 1 秒',
      '下放时控制，不要直接松手放回',
      '全程核心收紧，背部不要弓',
    ],
  },
  { exerciseKey: 'preset_dumbbell_row', name: '哑铃划船', icon: 'lucide:rows-3', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 90, category: 'back', weight: 10, quickReps: [8, 10, 12],
    tips: [
      '单手单膝撑在长凳上，另一侧脚踩地，背部与地面大致平行',
      '持哑铃的手臂自然下垂，肩胛先放松下沉',
      '以背阔肌发力，将哑铃向后上方拉至腰侧，肘部贴近身体',
      '顶峰挤压肩胛骨，停顿 1 秒',
      '下放时控制速度，充分拉伸背部（不要直接松掉）',
      '全程背部挺直、核心收紧，避免用惯性甩起或转体借力',
    ],
  },
  { exerciseKey: 'preset_deadlift', name: '硬拉', icon: 'lucide:weight', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 90, category: 'back', quickReps: [3, 5, 8],
    tips: [
      '双脚与髋同宽，杠铃靠近小腿',
      '屈髋下蹲握杠，背部挺直锁定',
      '起身时腿先发力，髋部打开，最后伸直',
      '全程杠铃贴近身体（沿着腿向上）',
      '顶部站直即可，不要后仰',
      '下放：先屈髋送臀，再屈膝；务必保护腰部，宁可减重也不要弓腰',
    ],
  },

  // ---- Shoulders ----
  { exerciseKey: 'dumbbell_press', name: '哑铃上举', icon: 'lucide:arrow-up-from-line', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'shoulders', weight: 5, quickReps: [8, 10, 12],
    tips: [
      '坐姿或站姿均可，核心收紧',
      '起始位置：哑铃在耳侧，肘关节 90°',
      '推起时双手轨迹略向中线靠拢，顶部不要锁肘',
      '下放时控制，肘部不要低于肩',
      '不要后倾借力（避免变成上斜卧推）',
      '呼吸：推起呼气、下放吸气',
    ],
  },
  { exerciseKey: 'preset_lateral_raise', name: '哑铃侧平举', icon: 'lucide:move-horizontal', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 45, category: 'shoulders', weight: 3, quickReps: [10, 12, 15],
    tips: [
      '站姿，膝盖微弯，核心收紧',
      '手肘略弯 (10-15°)，全程保持这个角度',
      '抬起至与肩平（手肘略高于手腕）',
      '小指略高于拇指（"倒水"姿势）',
      '动作慢且受控，不要靠惯性甩起来',
      '重量宁轻不重，质量优先',
    ],
  },
  { exerciseKey: 'preset_front_raise', name: '哑铃前平举', icon: 'lucide:arrow-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 45, category: 'shoulders', weight: 3, quickReps: [10, 12, 15],
    tips: [
      '站姿，双手哑铃放于大腿前',
      '手臂伸直略弯，向前抬起至与肩平',
      '不要超过头部，避免肩关节过度压力',
      '全程控制，下放比上抬慢',
      '不要借助身体后仰发力',
      '单手交替做可更专注',
    ],
  },
  { exerciseKey: 'preset_upright_row', name: '直立划船', icon: 'lucide:arrow-up-narrow-wide', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'shoulders', weight: 10, quickReps: [8, 10, 12],
    tips: [
      '站姿，双手与肩同宽握杠或哑铃',
      '沿身体提拉至胸口附近，肘部高于手腕',
      '主动用三角肌带动，不只用手臂',
      '不要拉过下巴，避免肩部撞击',
      '下放时控制，背部保持挺直',
      '肩部有不适立即调整或停止',
    ],
  },

  // ---- Arms ----
  { exerciseKey: 'dumbbell_curl', name: '哑铃弯举', icon: 'lucide:dumbbell', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'arms', weight: 5, quickReps: [10, 12, 15],
    tips: [
      '上臂始终紧贴身体两侧，不要前后摆动借力',
      '手肘固定不动，只由前臂带动哑铃移动',
      '顶峰位置停留 1 秒，主动挤压肱二头肌',
      '缓慢放回到大臂垂直地面，离心阶段比向上阶段更重要',
      '呼吸节奏：向上呼气、向下吸气',
      '选择能完成目标次数且最后 2 次有挑战的重量；宁可降重量也要避免借力',
    ],
  },
  { exerciseKey: 'preset_barbell_curl', name: '杠铃弯举', icon: 'lucide:dumbbell', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'arms', weight: 15, quickReps: [8, 10, 12],
    tips: [
      '站姿，双手与肩同宽握杠（正握）',
      '上臂紧贴身体，肘部固定为支点',
      '用肱二头肌带动杠铃上升',
      '顶峰停顿挤压',
      '下放慢且控制，离心阶段同样要发力',
      '避免身体前后摆动借力；宁可减重保持姿势',
    ],
  },
  { exerciseKey: 'preset_dip', name: '双杠臂屈伸', icon: 'lucide:arrow-down-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'arms', quickReps: [5, 8, 10],
    tips: [
      '直臂支撑，肩部下沉（不要耸肩）',
      '略前倾偏重胸部、垂直时偏重三头肌',
      '下降至肘关节约 90°（不要过低）',
      '起身时不锁肘，保留肌肉张力',
      '全程肩胛骨下沉，保护肩部',
      '核心收紧，腿部不要乱晃',
    ],
  },

  // ---- Legs ----
  { exerciseKey: 'squat', name: '深蹲', icon: 'lucide:arrow-down-to-line', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'legs', quickReps: [10, 15, 20],
    tips: [
      '双脚与肩同宽或略宽，脚尖略外八',
      '下蹲时膝盖方向与脚尖一致',
      '大腿至少与地面平行（能做到更低更好）',
      '全程保持背部挺直，不要弓腰',
      '重心在脚后跟和脚掌之间',
      '起身时臀腿同时发力，呼气',
    ],
  },
  { exerciseKey: 'preset_dumbbell_squat', name: '哑铃深蹲', icon: 'lucide:arrow-down', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'legs', weight: 10, quickReps: [10, 12, 15],
    tips: [
      '双手垂直握哑铃于身体两侧（或抱于胸前）',
      '动作要领同徒手深蹲',
      '加重后更要注意背部姿势，宁可减重',
      '下蹲速度均匀，起身时髋膝同时发力',
      '哑铃放置位置不影响动作即可',
      '进阶可尝试高脚杯式（goblet squat）',
    ],
  },
  { exerciseKey: 'preset_lunge', name: '弓步蹲', icon: 'lucide:step-forward', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'legs', quickReps: [10, 12, 15],
    tips: [
      '一脚向前迈一大步，前后脚距约一腿长',
      '下蹲时后腿膝盖几乎触地，前腿大腿水平',
      '前腿膝盖不超过脚尖',
      '上身保持挺直，不要前倾',
      '起身时用前腿发力',
      '左右交替进行，每边记一组',
    ],
  },
  { exerciseKey: 'glute_bridge', name: '臀桥', icon: 'lucide:move-up', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'legs', weight: 0, quickReps: [12, 15, 20],
    tips: [
      '仰卧，膝盖弯曲约 90°，双脚踩地',
      '双手放身体两侧或抱于胸前',
      '主动收紧臀部，把髋部抬至与膝盖平',
      '顶部挤压臀部 1 秒',
      '下放时控制，臀部不完全触地（保持张力）',
      '注意是臀部发力，不是腰部',
    ],
  },
  { exerciseKey: 'preset_single_leg_glute_bridge', name: '单腿臀桥', icon: 'lucide:arrow-up-right', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'legs', quickReps: [10, 12, 15],
    tips: [
      '仰卧屈膝，单脚踩地，另一脚抬起伸直',
      '用踩地的那只脚发力顶起髋部',
      '保持骨盆水平，不要倾斜',
      '顶部挤压臀部 1 秒',
      '下放时控制',
      '难度比双腿臀桥大，臀部刺激更强',
    ],
  },
  { exerciseKey: 'preset_high_knees', name: '高抬腿', icon: 'lucide:chevrons-up', template: 'countdown', exerciseType: 'anaerobic', restDuration: 30, category: 'legs', defaultDuration: 30,
    tips: [
      '站立，原地交替高抬腿',
      '大腿抬至与地面平行',
      '全程脚尖/前脚掌着地，落地轻盈',
      '双手前后摆动配合',
      '核心收紧，上身保持直立',
      '节奏越快越好，注意呼吸均匀',
    ],
  },

  // ---- Core ----
  { exerciseKey: 'plank', name: '平板支撑', icon: 'lucide:activity', template: 'countdown', exerciseType: 'anaerobic', restDuration: 30, category: 'core', defaultDuration: 60,
    tips: [
      '前臂着地，肘关节正上方为肩（上臂垂直地面）',
      '身体一条直线：耳朵-肩-髋-踝',
      '核心主动收紧，臀部不上翘也不下沉',
      '双脚分开略小于肩宽',
      '呼吸均匀，不要憋气',
      '姿势比时长重要，姿势变形时立即停止',
    ],
  },
  { exerciseKey: 'preset_sit_up', name: '仰卧起坐', icon: 'lucide:fold-vertical', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 45, category: 'core', quickReps: [10, 15, 20],
    tips: [
      '仰卧，膝盖弯曲约 90°',
      '双手放胸前或耳侧（不要拽脖子）',
      '用腹肌带动上身起来',
      '上抬约 30-45° 即可，不必到完全坐直',
      '下放慢，保持核心张力',
      '避免靠惯性甩起来',
    ],
  },
  { exerciseKey: 'preset_crunch', name: '卷腹', icon: 'lucide:arrow-up-narrow-wide', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 45, category: 'core', quickReps: [10, 15, 20],
    tips: [
      '仰卧屈膝，起始姿势同仰卧起坐',
      '只抬起肩胛骨离地，下背仍贴地',
      '用腹肌主动收缩，下巴不贴胸',
      '顶部挤压 1 秒',
      '比仰卧起坐对腹肌刺激更直接',
      '双手不要拽脖子，避免颈部受力',
    ],
  },
  { exerciseKey: 'preset_mountain_climber', name: '登山者', icon: 'lucide:mountain', template: 'countdown', exerciseType: 'anaerobic', restDuration: 30, category: 'core', defaultDuration: 30,
    tips: [
      '起始姿势同俯卧撑撑住',
      '交替将膝盖快速带向胸口',
      '全程保持髋部稳定，不上下大幅起伏',
      '核心持续收紧',
      '节奏越快越能锻炼心肺',
      '注意手腕承重，不适及时调整',
    ],
  },

  // ---- Full body ----
  { exerciseKey: 'preset_burpee', name: '波比跳', icon: 'lucide:flame', template: 'manual_reps', exerciseType: 'anaerobic', restDuration: 60, category: 'full_body', quickReps: [5, 10, 15],
    tips: [
      '起立 → 下蹲 → 撑地变俯卧撑姿势 → 俯卧撑（可选）→ 跳回 → 起立跳起',
      '全程节奏流畅，不要每步停顿太久',
      '撑地变姿势时核心收紧，不塌腰',
      '跳起时双手举过头',
      '落地轻盈，膝盖微弯吸震',
      '强度极大，注意呼吸和心率',
    ],
  },
  { exerciseKey: 'preset_jumping_jacks', name: '开合跳', icon: 'lucide:scaling', template: 'countdown', exerciseType: 'anaerobic', restDuration: 30, category: 'full_body', defaultDuration: 45,
    tips: [
      '站立，双脚并拢',
      '跳起同时双脚分开略宽于肩，双手举过头',
      '跳回并拢',
      '落地轻盈，前脚掌着地',
      '核心收紧，背部挺直',
      '节奏均匀，配合呼吸',
    ],
  },
]

export const PRESET_BY_KEY: Record<string, PresetExercise> = PRESET_EXERCISES.reduce((acc, p) => {
  acc[p.exerciseKey] = p
  return acc
}, {} as Record<string, PresetExercise>)

// ============================================================================
// Icon palette for custom exercises — lucide icons matching the built-in style
// ============================================================================

export const CUSTOM_ICON_CHOICES: string[] = [
  'lucide:dumbbell',
  'lucide:activity',
  'lucide:bike',
  'lucide:footprints',
  'lucide:heart-pulse',
  'lucide:flame',
  'lucide:zap',
  'lucide:target',
  'lucide:timer',
  'lucide:move',
  'lucide:arrow-up-from-line',
  'lucide:arrow-down-to-line',
]

// ============================================================================
// Template metadata
// ============================================================================

export interface TemplateInfo {
  template: CustomExerciseTemplate
  label: string
  description: string
  defaultType: ExerciseType
  needsRestDuration: boolean
  defaultRest: number
  /** Has a "default per-set duration" field — only countdown needs it. */
  needsDefaultDuration?: boolean
  defaultDuration?: number
  /** Has an optional weight field (kg) — only sets_with_rest. */
  supportsWeight?: boolean
  defaultWeight?: number
  /** Default quick-tap reps for manual_reps template. Three slots only. */
  defaultQuickReps?: [number, number, number]
}

export const TEMPLATES: TemplateInfo[] = [
  { template: 'timer', label: '计时（开始-结束）', description: '类似骑车，按开始计时，按结束记录时长。', defaultType: 'aerobic', needsRestDuration: false, defaultRest: 30 },
  { template: 'laps_timer', label: '计时 + 计圈', description: '类似爬楼梯/跑步，计时中可记录每圈/每段用时。', defaultType: 'aerobic', needsRestDuration: false, defaultRest: 30 },
  { template: 'countdown', label: '倒计时', description: '类似平板支撑，设定时长后倒计时。', defaultType: 'anaerobic', needsRestDuration: true, defaultRest: 30, needsDefaultDuration: true, defaultDuration: 60 },
  { template: 'manual_reps', label: '组数 + 次数', description: '类似俯卧撑/哑铃弯举：每组记录次数，可选记录重量；提供 3 个快捷次数按钮。', defaultType: 'anaerobic', needsRestDuration: true, defaultRest: 60, defaultQuickReps: [10, 15, 20], supportsWeight: true, defaultWeight: 0 },
]

export const TEMPLATE_BY_KEY: Record<CustomExerciseTemplate, TemplateInfo> = TEMPLATES.reduce((acc, t) => {
  acc[t.template] = t
  return acc
}, {} as Record<CustomExerciseTemplate, TemplateInfo>)

// ============================================================================
// Lookup helpers
// ============================================================================

function customToMeta(c: LocalCustomExercise): ExerciseMeta {
  return {
    exerciseKey: c.exerciseKey,
    label: c.name,
    icon: c.icon,
    color: c.exerciseType === 'aerobic' ? 'mint' : 'violet',
    exerciseType: c.exerciseType,
    path: pageLinks.CustomExercise(c.exerciseKey),
    isCustom: true,
    template: c.template,
    restDuration: c.restDuration,
  }
}

/** Get metadata for a single exercise key (all exercises live in customExercises now). */
export async function getExerciseMeta(exerciseKey: string): Promise<ExerciseMeta | null> {
  const custom = await db.customExercises.get(exerciseKey)
  if (custom) return customToMeta(custom)
  return null
}

/** All active customs (excludes pendingDelete). */
export async function listActiveCustoms(): Promise<ExerciseMeta[]> {
  const rows = await db.customExercises.toArray()
  return rows.filter(r => !r.pendingDelete).map(customToMeta)
}
